import React, { useMemo, useState } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis } from "recharts";
import { ChartConfig, ChartContainer } from "@/components/ui/chart";

export type HourlyBarDatum = {
  label: string;
  primary: number;
  secondary: number;
  total?: number;
};

const EMPTY_DATA: HourlyBarDatum[] = [];

const TAN = "#CDC9BB";
const TRACK = "#E9E9E9";
const TRACK_LEGEND_DOT = "#D4D4D8";
const OUTLINE = "var(--eathy-comfort-1)";
const R = 10;
const MIN_ZERO_VISUAL_PERCENT = 10;
const MIN_NON_ZERO_VISUAL_PERCENT = 24;

type ChartRow = {
  label: string;
  primaryValue: number;
  totalValue: number;
  totalPercent: number;
  fillPercent: number;
  isZeroTotal: boolean;
};

type BarBg = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
};

type TrackerBarShapeProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  index?: number;
  background?: BarBg;
  hoveredIndex: number | null;
  payload?: ChartRow;
};

function tanSegmentPath(
  x: number,
  yTop: number,
  w: number,
  h: number,
  r: number,
): string {
  if (h <= 0) {
    return "";
  }
  const rr = Math.min(r, w / 2, h / 2);
  return [
    `M ${x + rr} ${yTop}`,
    `L ${x + w - rr} ${yTop}`,
    `Q ${x + w} ${yTop} ${x + w} ${yTop + rr}`,
    `L ${x + w} ${yTop + h - rr}`,
    `Q ${x + w} ${yTop + h} ${x + w - rr} ${yTop + h}`,
    `L ${x + rr} ${yTop + h}`,
    `Q ${x} ${yTop + h} ${x} ${yTop + h - rr}`,
    `L ${x} ${yTop + rr}`,
    `Q ${x} ${yTop} ${x + rr} ${yTop}`,
    "Z",
  ].join(" ");
}

function TrackerBarShape({
  x,
  y,
  width,
  height,
  index = 0,
  hoveredIndex,
  payload,
}: TrackerBarShapeProps) {
  if (height <= 0) {
    return null;
  }
  const outline = hoveredIndex === index;
  const fillPercent = payload?.fillPercent ?? 0;
  const isZeroTotal = payload?.isZeroTotal ?? false;
  if (isZeroTotal) {
    const radius = width / 2;
    const cx = x + radius;
    const cy = y + height - radius;
    return (
      <g className="recharts-bar-rectangle">
        <circle cx={cx} cy={cy} r={radius} fill={TRACK} />
        {outline ? (
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={OUTLINE}
            strokeWidth={2}
          />
        ) : null}
      </g>
    );
  }
  const tanHeight = Math.round((fillPercent / 100) * height);
  const tanY = y + height - tanHeight;
  const d = tanSegmentPath(x, tanY, width, tanHeight, R);

  return (
    <g className="recharts-bar-rectangle">
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={R}
        ry={R}
        fill={TRACK}
      />
      {d ? <path d={d} fill={TAN} /> : null}
      {outline ? (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={R}
          ry={R}
          fill="none"
          stroke={OUTLINE}
          strokeWidth={2}
        />
      ) : null}
    </g>
  );
}

function LegendTooltip({
  active,
  payload,
  primaryLabel,
  secondaryLabel,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ChartRow }>;
  primaryLabel: string;
  secondaryLabel: string;
}) {
  const row = payload?.[0]?.payload;

  if (!active || !row) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-zinc-100 bg-white px-3 py-2 text-xs shadow-md">
      <div className="flex items-center justify-between gap-4 font-normal text-neutral-600">
        <span className="flex items-center gap-2">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: TRACK_LEGEND_DOT }}
            aria-hidden
          />
          <span>{primaryLabel}</span>
        </span>
        <span className="font-medium text-neutral-900">{row.totalValue}</span>
      </div>
      <div className="flex items-center justify-between gap-4 font-normal text-neutral-600">
        <span className="flex items-center gap-2">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: TAN }}
            aria-hidden
          />
          <span>{secondaryLabel}</span>
        </span>
        <span className="font-medium text-neutral-900">{row.primaryValue}</span>
      </div>
    </div>
  );
}

interface DashboardHourlyBarChartProps {
  primaryLabel: string;
  secondaryLabel: string;
  data?: HourlyBarDatum[];
  metricScale?: number;
}

export const DashboardHourlyBarChart: React.FC<
  DashboardHourlyBarChartProps
> = ({ primaryLabel, secondaryLabel, data = EMPTY_DATA }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const chartConfig: ChartConfig = useMemo(
    () => ({
      track: { label: primaryLabel, color: TRACK },
      totalPercent: { label: secondaryLabel, color: TAN },
    }),
    [primaryLabel, secondaryLabel],
  );

  const chartData: ChartRow[] = useMemo(() => {
    const totals = data.map((d) =>
      Math.max(d.total ?? d.primary + d.secondary, d.primary),
    );
    const maxTotal = Math.max(...totals, 1);
    return data.map((d) => {
      const total = d.total ?? d.primary + d.secondary;
      const visualTotal = Math.max(total, d.primary);
      let totalPercent: number;
      if (visualTotal === 0) {
        totalPercent = MIN_ZERO_VISUAL_PERCENT;
      } else {
        const share = visualTotal / maxTotal;
        totalPercent = Math.round(
          MIN_NON_ZERO_VISUAL_PERCENT +
            share * (100 - MIN_NON_ZERO_VISUAL_PERCENT),
        );
      }
      const fillPercent =
        visualTotal === 0
          ? 0
          : Math.min(100, Math.round((d.primary / visualTotal) * 100));
      return {
        label: d.label,
        primaryValue: d.primary,
        totalValue: total,
        totalPercent,
        fillPercent,
        isZeroTotal: visualTotal === 0,
      };
    });
  }, [data]);

  return (
    <ChartContainer
      config={chartConfig}
      className="font-geist !aspect-auto h-full w-full"
    >
      <BarChart
        accessibilityLayer
        data={chartData}
        margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
        onMouseMove={(state) => {
          const idx = state?.activeTooltipIndex;
          setHoveredIndex(typeof idx === "number" && idx >= 0 ? idx : null);
        }}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          interval={0}
          tick={({ x, y, payload, index }) => {
            const i = typeof index === "number" ? index : 0;
            const emphasized = hoveredIndex === i;
            return (
              <text
                x={x}
                y={y}
                dy={14}
                textAnchor="middle"
                fill={emphasized ? "#171717" : "#A3A3A3"}
                fontSize={12}
                fontWeight={emphasized ? 600 : 400}
              >
                {payload.value}
              </text>
            );
          }}
        />
        <YAxis hide domain={[0, 100]} />
        <Tooltip
          cursor={false}
          isAnimationActive={false}
          allowEscapeViewBox={{ x: true, y: true }}
          wrapperStyle={{ zIndex: 50 }}
          offset={12}
          content={({ active, payload }) => (
            <LegendTooltip
              active={active}
              payload={payload as Array<{ payload?: ChartRow }> | undefined}
              primaryLabel={primaryLabel}
              secondaryLabel={secondaryLabel}
            />
          )}
        />
        <Bar
          dataKey="totalPercent"
          barSize={20}
          fill={TAN}
          isAnimationActive={false}
          shape={(props: unknown) => (
            <TrackerBarShape
              {...(props as TrackerBarShapeProps)}
              hoveredIndex={hoveredIndex}
            />
          )}
        />
      </BarChart>
    </ChartContainer>
  );
};
